<script setup lang="ts">
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import LoadForecastChart from '@/components/cpm/LoadForecastChart.vue'
import LoadForecastTable from '@/components/cpm/LoadForecastTable.vue'

import { DateTime } from 'luxon'
import { ref, onMounted } from 'vue'

import type { ILoadForecastFilters } from '@/types/cpm/filters'
import { formatISODateToDateString } from '@/util/formatters'
import { getLoadForecastTimeseries } from '@/services/cpm/cpm.service'
import { ISOs } from '@/types/core/Site'
import { NotifyFail } from '@/helpers/core/notifications'

const RevolveTimezone = 'US/Central'

// Day-1 (forecast issued the day before the operating day) is the view the data analysts work from
const initialDaysAhead = 1

const firstDayOfCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
const lastDayOfCurrentMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
const initialStart = DateTime.fromJSDate(firstDayOfCurrentMonth).setZone(RevolveTimezone)
const initialEnd = DateTime.fromJSDate(lastDayOfCurrentMonth).setZone(RevolveTimezone)

const loading = ref<boolean>(true)

const chartData = ref([])
const tableData = ref([])

const getChartAndTableData = async (filters: ILoadForecastFilters) => {
  loading.value = true
  try {
    const data = await getLoadForecastTimeseries(filters)
    chartData.value = data
    tableData.value = data
  } catch (err: any) {
    NotifyFail(err.message)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  getChartAndTableData({
    iso: ISOs.MISO,
    load_resource_zone: 'Z1',
    timezone: 'US/Central',
    start_date: formatISODateToDateString(initialStart.toISO() as string),
    end_date: formatISODateToDateString(initialEnd.toISO() as string),
    days_ahead: initialDaysAhead,
  })
})
</script>

<template>
  <ViewLayout class="load-forecast-layout">
    <LoadForecastChart
      :loading="loading"
      :initial-start="initialStart"
      :initial-end="initialEnd"
      :forecast-data="chartData"
      :initial-days-ahead="initialDaysAhead"
      @filters-change="getChartAndTableData"
    />
    <LoadForecastTable :data="tableData" :loading="loading" />
  </ViewLayout>
</template>

<style lang="scss" scoped>
.load-forecast-layout {
  height: 100vh;
  overflow-y: auto;
}
</style>
